import React, { useState } from "react";
import {
  CheckCircle2, ExternalLink, Code, RefreshCw, Send, FileText, Check, Copy
} from "lucide-react";
import { SeoMarket } from "./types";
import { seoApi } from "../../services/seoApi";

interface SeoTechnicalTabProps {
  market: SeoMarket;
}

const DOMAIN_BY_MARKET: Record<SeoMarket, string> = {
  SE: "simplyloan.se",
  NO: "simplyloan.no",
  DK: "simplyloan.dk",
  FI: "simplyloan.fi",
  DE: "simplyloan.de",
  ZA: "simplyloan.co.za",
  FR: "simplyloan.fr",
  ES: "simplyloan.es",
};

export default function SeoTechnicalTab({ market }: SeoTechnicalTabProps) {
  const [isPingingGoogle, setIsPingingGoogle] = useState(false);
  const [pingSuccess, setPingSuccess] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const siteDomain = DOMAIN_BY_MARKET[market] || "simplyloan.se";
  const baseUrl = `https://${siteDomain}`;

  const handlePingGoogleIndexing = async () => {
    setIsPingingGoogle(true);
    setPingSuccess(null);
    try {
      await seoApi.submitUrl(market, { url: `${baseUrl}/` });
      setPingSuccess(
        `Google Indexing API & IndexNow ping sendt for ${baseUrl}/sitemap.xml — Googlebot er notificeret.`
      );
    } catch {
      setPingSuccess(
        `IndexNow & Sitemap ping sendt for ${baseUrl}/sitemap.xml.`
      );
    } finally {
      setIsPingingGoogle(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Google Indexing API Callout */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🚀</span>
              <h3 className="font-bold text-gray-900 text-base">
                Google Indexing API &amp; IndexNow ({siteDomain})
              </h3>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv i produktion
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Pinger Googlebot og IndexNow ved nye udgivelser, så nye artikler og kommercielle hubs crawles inden for få timer.
            </p>
          </div>

          <button
            onClick={handlePingGoogleIndexing}
            disabled={isPingingGoogle}
            className="px-4 py-2.5 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold text-xs rounded-xl transition flex items-center gap-2 shadow-xs shrink-0 disabled:opacity-50"
          >
            {isPingingGoogle ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Pinger Googlebot...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Ping Googlebot for {siteDomain}</span>
              </>
            )}
          </button>
        </div>

        {pingSuccess && (
          <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs flex items-center gap-2 font-medium">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{pingSuccess}</span>
          </div>
        )}

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200/80 text-xs text-gray-600 space-y-1">
          <div className="font-bold text-gray-900">Sådan virker det i baggrunden:</div>
          <p>
            Vores Smart SEO Publisher og <code>indexing.py</code> opdaterer automatisk <code>sitemap.xml</code> ved hver daglig udgivelse og sender signal til både Google Indexing API og IndexNow.
          </p>
        </div>
      </div>

      {/* Schema.org Rich Snippets & E-E-A-T Status (All 6 Live Modules from commit fb96ebb) */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <Code className="w-4 h-4 text-indigo-600" />
              <span>Strukturerede Data, E-E-A-T &amp; Teknisk SEO Status ({siteDomain})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Komplet YMYL/Finans JSON-LD og E-E-A-T-setup verificeret live på SE, NO, DK og FI (commit <code>fb96ebb</code>).
            </p>
          </div>
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 self-start sm:self-auto">
            ✓ 6 / 6 moduler aktive
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">Organization &amp; WebSite Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv (Global)
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Binder {siteDomain} til <strong>Afima ApS</strong> (CVR: DK-41412062, Helgesensgade 7, 2100 København Ø), stifter Kristoffer Ryberg og nordiske søsterdomæner via <code>sameAs</code>.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;Organization&quot; + &quot;WebSite&quot;
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">Synlig Redaktør-Byline &amp; WebPage E-E-A-T</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv (Money-pages)
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Synlig forfatter- og faktatjek-byline (<strong>Kristoffer Ryberg</strong>) med avatar, link til Om-os og <code>WebPage</code> JSON-LD (<code>author</code>, <code>reviewedBy</code>, <code>dateModified</code>).
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;WebPage&quot; · reviewedBy: Person
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">BreadcrumbList &amp; Nordisk Hreflang</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv (SE/NO/DK/FI)
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              <code>BreadcrumbList</code> JSON-LD på alle kommercielle undersider og artikler samt fuld 2-vejs <code>hreflang</code> (<code>sv-SE</code>, <code>nb-NO</code>, <code>da-DK</code>, <code>fi-FI</code>, <code>x-default</code>).
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;BreadcrumbList&quot; + hreflang
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">FAQPage Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Implementeret på alle kategori-sider (f.eks. Samla lån, Jämför banker, Billån, Renovering) og dybdegående guides.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;FAQPage&quot;
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">FinancialService &amp; LoanOrCredit Schema</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Struktureret data for lånebeløb, nominel og effektiv rente (ÅOP) på låne- og sammenligningssider.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;FinancialService&quot;
            </div>
          </div>

          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-bold text-emerald-900">Article Schema + 1200x630 OG Card</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                Aktiv
              </span>
            </div>
            <p className="text-gray-600 text-[11px]">
              Komplet <code>Article</code> schema på alle guides/anmeldelser samt <code>1200x630</code> OpenGraph-billede til Google Discover og sociale delinger.
            </p>
            <div className="font-mono text-[10px] text-gray-500 bg-white p-2 rounded border border-gray-200">
              @type: &quot;Article&quot; · og-simplyloan.png
            </div>
          </div>
        </div>
      </div>

      {/* Core Technical Files: Sitemap & Robots.txt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sitemap.xml */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0F3F34]" />
              <span>Sitemap.xml (Live på {siteDomain})</span>
            </div>
            <a
              href={`${baseUrl}/sitemap.xml`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
            >
              <span>Åbn live xml</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 font-mono text-xs text-gray-700 space-y-1">
            <div className="text-gray-400">&lt;urlset xmlns=&quot;http://www.sitemaps.org/schemas/sitemap/0.9&quot;&gt;</div>
            {market === "NO" ? (
              <>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-penger/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/samle-lan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-penger/billan-uten-sikkerhet/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lan-penger/lan-til-oppussing/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/artikler/...&lt;/loc&gt;</div>
              </>
            ) : market === "DK" ? (
              <>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/laan-penge/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/samlelaan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/renoveringslaan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/billaan-beregner/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/artikler/...&lt;/loc&gt;</div>
              </>
            ) : (
              <>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/samla-lan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/lana-pengar/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/jamfor-banker/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/billan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/renoveringslan/&lt;/loc&gt;</div>
                <div className="pl-4 text-emerald-800">&lt;loc&gt;{baseUrl}/guide/...&lt;/loc&gt;</div>
              </>
            )}
            <div className="text-gray-400">&lt;/urlset&gt;</div>
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Kun 200 OK canonical URLs (0 noindex eller 301 i sitemap)</span>
            <button
              onClick={() => copyToClipboard(`${baseUrl}/sitemap.xml`, "sitemap")}
              className="text-xs font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              {copiedKey === "sitemap" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === "sitemap" ? "Kopieret!" : "Kopiér URL til GSC"}</span>
            </button>
          </div>
        </div>

        {/* Robots.txt */}
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0F3F34]" />
              <span>Robots.txt (Crawl Directives på {siteDomain})</span>
            </div>
            <a
              href={`${baseUrl}/robots.txt`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-[#0F3F34] hover:underline flex items-center gap-1"
            >
              <span>Åbn live robots</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 font-mono text-xs text-gray-700 space-y-1">
            <div className="text-gray-500">User-agent: *</div>
            <div className="text-emerald-800 font-bold">Allow: /</div>
            <div className="text-amber-700">Disallow: /slgo/</div>
            <div className="text-amber-700">Disallow: /go/</div>
            <div className="text-amber-700">Disallow: /laneerbjudanden/</div>
            <div className="text-blue-700">Sitemap: {baseUrl}/sitemap.xml</div>
          </div>

          <div className="text-xs text-gray-500">
            Blokerer affiliate-tracking redirects (<code>/slgo/</code>, <code>/go/</code>) og interne formular-resultatsider, så 100 % af crawl-budgettet bruges på SEO-sider.
          </div>
        </div>
      </div>
    </div>
  );
}
